/**
 * Relay API Client
 *
 * Handles all HTTP requests to the Relay backend API.
 * Self-sufficient auth pattern — singleton client, no token injection needed.
 * All snake_case → camelCase conversion happens here.
 */

import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import type {
  AcceptSuggestionResult,
  AssociationSuggestion,
  ConfidenceBand,
  ClassificationSource,
  Thread,
  ThreadAssociation,
  ThreadIngestProvenance,
  ThreadListResponse,
  PatchThreadBody,
  AddAssociationBody,
  EmailMessage,
  TeamsMessage,
  TeamsMessageSummary,
  CalendarMeta,
  Signal,
  SignalStatus,
  Task,
  CreateTaskBody,
  UpdateTaskBody,
  ListTasksParams,
  TraceParams,
  TraceResponse,
  TraceThreadCard,
  TraceSignal,
  TraceTask,
  IngestRequest,
  IngestResponse,
  IngestAttachmentSummary,
  EmailAttachment,
  PendingTenantAttachment,
  AutoPromoteThreadAttachmentsResult,
  AttachmentStatus,
  AttachmentResourceKind,
  ListAttachmentsResponse,
  AttachmentContentUrl,
  AttachmentSource,
  RelayNote,
  CreateNoteBody,
  TraceNoteSummary,
  RelayPin,
  CreatePinBody,
  UpdatePinBody,
  TagCandidate,
  TagCandidateMention,
  TagMintField,
  AcceptTagCandidateResult,
} from '../types/relay-types';

/* eslint-disable @typescript-eslint/naming-convention */
type RawThreadAssociation = {
  id: string;
  object_type_id: string;
  object_type_name: string | null;
  object_record_id: string | null;
  object_record_code: string | null;
  object_record_name: string | null;
  is_required_type: boolean;
  confidence?: number | null;
  confidence_band?: ConfidenceBand | null;
  classification_source?: ClassificationSource | null;
  reason?: string | null;
  auto_confirmed_at?: string | null;
  created_at: string;
  created_by: string | null;
};

type RawAssociationSuggestion = {
  id: string;
  thread_id: string;
  kind: 'link';
  object_type_id: string;
  object_type_name: string | null;
  object_record_id: string | null;
  object_record_code: string | null;
  object_record_name: string | null;
  confidence: number;
  confidence_band: ConfidenceBand;
  reason: string | null;
  status: 'pending' | 'accepted' | 'rejected';
};

type RawThreadLatestSignal = {
  id: string;
  content: string;
  status: string;
  created_at: string;
};

type RawThread = {
  id: string;
  tenant_id: string;
  subject: string | null;
  associations: RawThreadAssociation[];
  pending_suggestions?: RawAssociationSuggestion[] | null;
  latest_signal?: RawThreadLatestSignal | null;
  signal_count?: number | null;
  teams_message_count?: number | null;
  association_confirmed: boolean;
  auto_confirmed?: boolean | null;
  needs_review: boolean;
  confirmed_by: string | null;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
  last_content_activity_at?: string | null;
  deleted_at?: string | null;
  ingest_provenance?: RawThreadIngestProvenance | null;
};

type RawThreadIngestProvenance = {
  kind: string;
  detail: string | null;
  direction: string | null;
  decided_at: string;
};

type RawMessage = {
  id: string;
  thread_id: string;
  sender: string;
  recipients: {to: string[]; cc: string[]; bcc: string[]};
  subject: string | null;
  sent_at: string | null;
  raw_body: string;
  raw_headers: Record<string, unknown> | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  classification_source: 'manual' | 'auto_inherited' | null;
  body_cleaned: string | null;
  hash_cleaned: string | null;
  cleaner_version: string | null;
  cleaner_rules_fired: Array<[string, number]> | null;
  tail_start: number | null;
  tail_end: number | null;
  tail_status: string;
  tail_confidence: string | null;
  tail_reason: string | null;
  tail_extractor_version: string | null;
  tail_body_hash: string | null;
  message_category: 'calendar' | null;
  calendar: RawCalendarMeta | null;
  created_at: string;
  updated_at: string;
};

type RawTeamsMessage = {
  id: string;
  tenant_id: string;
  thread_id: string;
  teams_message_id: string;
  reply_to_id: string | null;
  surface_kind: 'chat' | 'channel';
  teams_conversation_id: string;
  conversation_label: string | null;
  author_display_name: string;
  author_aad_id: string | null;
  author_upn: string | null;
  body_text: string;
  sent_at: string;
  web_url: string | null;
  ingested_by_user_id: string;
  extra_metadata: Record<string, unknown> | null;
  created_at: string;
};

type RawCalendarMeta = {
  uid: string | null;
  method: string | null;
  status: string | null;
  summary: string | null;
  dtstart: string | null;
  dtend: string | null;
  all_day: boolean;
  location: string | null;
  organizer: {name: string | null; email: string | null} | null;
  attendees: Array<{
    name: string | null;
    email: string | null;
    partstat: string | null;
  }> | null;
};

type RawSignal = {
  id: string;
  thread_id: string;
  status: string;
  content: string;
  source_message_ids: string[] | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  effective_timestamp: string;
};
type RawTask = {
  id: string;
  tenant_id: string;
  thread_id: string;
  source_signal_id: string | null;
  source_message_id: string | null;
  title: string;
  description: string | null;
  status: string;
  assigned_to: string | null;
  due_date: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};
type RawIngestAttachmentSummary = {
  id: string;
  email_message_id: string;
  filename: string;
  status: string;
  filter_reason: string | null;
};
type RawIngestResponse = {
  thread_id: string;
  thread_created: boolean;
  thread_association_confirmed: boolean;
  message_ids: string[];
  attachments?: RawIngestAttachmentSummary[];
};
type RawEmailAttachment = {
  id: string;
  tenant_id: string;
  email_message_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  status: string;
  image_signal: string | null;
  filter_reason: string | null;
  ingested_resource_kind: string | null;
  ingested_resource_id: string | null;
  ingested_at: string | null;
  ingested_by: string | null;
  dismissed_at: string | null;
  dismissed_by: string | null;
  dismiss_reason: string | null;
  created_at: string;
  updated_at: string;
};
type RawPendingTenantAttachment = RawEmailAttachment & {
  thread_id: string;
  thread_subject: string | null;
};
// Teams attachment rows are shaped like email ones, minus image_signal and with
// teams_message_id in place of email_message_id.
type RawTeamsAttachment = Omit<
  RawEmailAttachment,
  'email_message_id' | 'image_signal'
> & {teams_message_id: string};
type RawAutoPromoteResultItem = {
  attachment_id: string;
  file_id: string | null;
  status: string;
  reason: string | null;
};
type RawAutoPromoteResponse = {
  promoted: number;
  skipped: number;
  errors: number;
  results: RawAutoPromoteResultItem[];
};
type RawAttachmentContentUrl = {
  url: string;
  expires_at: string;
};
type RawAttachmentSource = {
  attachment_id: string;
  filename: string;
  thread_id: string;
  email_subject: string | null;
  email_sender: string;
  ingested_at: string | null;
};
type RawNoteAuthor = {
  id: string;
  display_name: string | null;
};
type RawNoteAssociation = {
  object_type_id: string;
  object_type_name: string | null;
  object_record_id: string | null;
  object_record_code: string | null;
  object_record_name: string | null;
};
type RawNoteFileRef = {
  ref_id: string;
  file_id: string;
  file_name: string;
  content_type: string | null;
};
type RawRelayNote = {
  id: string;
  body: string;
  author: RawNoteAuthor;
  associations: RawNoteAssociation[];
  file_refs: RawNoteFileRef[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};
type RawTraceNoteSummary = {
  id: string;
  author_user_id: string | null;
  body_preview: string | null;
  file_ref_count: number;
  file_ids: string[];
  created_at: string;
  updated_at: string;
};
type RawRequiredAssociationTypesResponse = {
  required_association_types: Array<{
    object_type_id: string;
    name: string;
    prefix: string;
  }>;
};
type RawTagCandidateMention = {
  id: string;
  thread_id: string;
  thread_subject: string | null;
  association_id: string;
  confidence: number | null;
  rationale: string;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
};

type RawTagCandidate = {
  id: string;
  object_type_id: string;
  object_type_name: string | null;
  display_name: string;
  best_confidence: number | null;
  rationale: string;
  status: 'pending' | 'accepted' | 'rejected';
  minted_record_id: string | null;
  mention_count: number;
  thread_count: number;
  distinct_sender_count: number;
  first_seen_at: string;
  last_seen_at: string;
  created_at: string;
  resolved_at: string | null;
  mentions: RawTagCandidateMention[];
};

type RawTagMintField = {
  field_key: string;
  label: string;
  field_type: string;
  options?: string[];
};

type RawPinRef = {
  object_type_id: string;
  object_record_id: string;
};
type RawPin = {
  id: string;
  label: string;
  kind: 'records';
  payload: {refs: RawPinRef[]};
  created_at: string;
  updated_at: string;
};
/* eslint-enable @typescript-eslint/naming-convention */

export class RelayApiClient extends AuthenticatedApiClient {
  private get basePath(): string {
    return '/api/v1/relay';
  }

  // -- Threads ---------------------------------------------------------------

  async getTriageThreads(params?: {
    limit?: number;
    offset?: number;
  }): Promise<ThreadListResponse> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const qp = new URLSearchParams({needs_review: 'true'});
    if (params?.limit !== undefined) qp.set('limit', String(params.limit));
    if (params?.offset !== undefined) qp.set('offset', String(params.offset));

    const raw = await this.get<RawThread[] | {items: RawThread[]}>(
      `${this.basePath}/threads?${qp.toString()}`
    );
    return this.normalizeThreadList(raw);
  }

  /** Fetch all threads (no needs_review filter). */
  async getAllThreads(params?: {
    limit?: number;
    offset?: number;
  }): Promise<ThreadListResponse> {
    const qp = new URLSearchParams();
    if (params?.limit !== undefined) qp.set('limit', String(params.limit));
    if (params?.offset !== undefined) qp.set('offset', String(params.offset));

    const qs = qp.toString();
    const url = qs
      ? `${this.basePath}/threads?${qs}`
      : `${this.basePath}/threads`;
    const raw = await this.get<RawThread[] | {items: RawThread[]}>(url);
    return this.normalizeThreadList(raw);
  }

  async getThread(id: string): Promise<Thread> {
    const raw = await this.get<RawThread>(`${this.basePath}/threads/${id}`);
    return this.toThread(raw);
  }

  async patchThread(id: string, body: PatchThreadBody): Promise<Thread> {
    const payload: Record<string, unknown> = {};
    if (body.subject !== undefined) payload.subject = body.subject;
    if (body.associationConfirmed !== undefined)
      payload.association_confirmed = body.associationConfirmed;

    const raw = await this.patch<RawThread>(
      `${this.basePath}/threads/${id}`,
      payload
    );
    return this.toThread(raw);
  }

  async deleteThread(id: string): Promise<void> {
    await this.delete(`${this.basePath}/threads/${id}`);
  }

  /**
   * Hide a thread and stop it being re-ingested. Distinct from
   * `deleteThread`, which purges — Remove keeps every row and is reversible
   * via `restoreThread`. Idempotent server-side.
   */
  async removeThread(id: string): Promise<Thread> {
    const raw = await this.post<RawThread>(
      `${this.basePath}/threads/${id}/remove`,
      {}
    );
    return this.toThread(raw);
  }

  async restoreThread(id: string): Promise<Thread> {
    const raw = await this.post<RawThread>(
      `${this.basePath}/threads/${id}/restore`,
      {}
    );
    return this.toThread(raw);
  }

  async searchThreads(query: string): Promise<ThreadListResponse> {
    const raw = await this.get<RawThread[] | {items: RawThread[]}>(
      `${this.basePath}/threads?search=${encodeURIComponent(query)}`
    );
    return this.normalizeThreadList(raw);
  }

  /** Threads associated with a specific MDM ObjectRecord. */
  async getThreadsForObject(
    objectTypeId: string,
    objectRecordId: string
  ): Promise<ThreadListResponse> {
    const qp = new URLSearchParams();
    qp.append('association_ref', `${objectTypeId}:${objectRecordId}`);
    const raw = await this.get<RawThread[] | {items: RawThread[]}>(
      `${this.basePath}/threads?${qp.toString()}`
    );
    return this.normalizeThreadList(raw);
  }

  // -- Messages --------------------------------------------------------------

  async getThreadMessages(threadId: string): Promise<EmailMessage[]> {
    const raw = await this.get<RawMessage[]>(
      `${this.basePath}/threads/${threadId}/messages`
    );
    return raw.map((m) => this.toMessage(m));
  }

  // -- Teams messages --------------------------------------------------------

  /** Ingested Microsoft Teams messages for a thread, `sent_at`-ascending
   *  (trusted order, like email). Read-only — Teams messages are immutable. */
  async getThreadTeamsMessages(threadId: string): Promise<TeamsMessage[]> {
    const raw = await this.get<RawTeamsMessage[]>(
      `${this.basePath}/threads/${threadId}/teams-messages`
    );
    return raw.map((m) => this.toTeamsMessage(m));
  }

  async reassignMessage(
    messageId: string,
    threadId: string
  ): Promise<EmailMessage> {
    const raw = await this.patch<RawMessage>(
      `${this.basePath}/messages/${messageId}`,
      {thread_id: threadId} // eslint-disable-line @typescript-eslint/naming-convention
    );
    return this.toMessage(raw);
  }

  async splitMessageToNewThread(messageId: string): Promise<Thread> {
    const raw = await this.post<RawThread>(
      `${this.basePath}/messages/${messageId}/split-to-new-thread`,
      {}
    );
    return this.toThread(raw);
  }

  // -- Signals ---------------------------------------------------------------

  async getThreadSignals(threadId: string): Promise<Signal[]> {
    const raw = await this.get<RawSignal[]>(
      `${this.basePath}/threads/${threadId}/signals`
    );
    return raw.map((s) => this.toSignal(s));
  }

  async confirmSignal(id: string): Promise<Signal> {
    const raw = await this.patch<RawSignal>(`${this.basePath}/signals/${id}`, {
      status: 'confirmed',
    });
    return this.toSignal(raw);
  }

  async dismissSignal(id: string): Promise<Signal> {
    const raw = await this.patch<RawSignal>(`${this.basePath}/signals/${id}`, {
      status: 'dismissed',
    });
    return this.toSignal(raw);
  }

  async createSignal(
    threadId: string,
    content: string,
    sourceMessageIds?: string[]
  ): Promise<Signal> {
    const body: Record<string, unknown> = {content};
    if (sourceMessageIds?.length) {
      body.source_message_ids = sourceMessageIds;
    }

    const raw = await this.post<RawSignal>(
      `${this.basePath}/threads/${threadId}/signals`,
      body
    );
    return this.toSignal(raw);
  }

  async editSignal(id: string, content: string): Promise<Signal> {
    const raw = await this.patch<RawSignal>(`${this.basePath}/signals/${id}`, {
      content,
    });
    return this.toSignal(raw);
  }

  // -- Tasks -----------------------------------------------------------------

  async createTask(threadId: string, body: CreateTaskBody): Promise<Task> {
    const payload: Record<string, unknown> = {title: body.title};
    if (body.description !== undefined) payload.description = body.description;
    if (body.sourceSignalId !== undefined)
      payload.source_signal_id = body.sourceSignalId;
    if (body.sourceMessageId !== undefined)
      payload.source_message_id = body.sourceMessageId;
    if (body.assignedTo !== undefined) payload.assigned_to = body.assignedTo;
    if (body.dueDate !== undefined) payload.due_date = body.dueDate;

    const raw = await this.post<RawTask>(
      `${this.basePath}/threads/${threadId}/tasks`,
      payload
    );
    return this.toTask(raw);
  }

  async getThreadTasks(
    threadId: string,
    includeArchived?: boolean
  ): Promise<Task[]> {
    const qp = new URLSearchParams();
    if (includeArchived) qp.set('include_archived', 'true');
    const qs = qp.toString();
    const raw = await this.get<RawTask[]>(
      `${this.basePath}/threads/${threadId}/tasks${qs ? `?${qs}` : ''}`
    );
    return raw.map((t) => this.toTask(t));
  }

  async listTasks(params: ListTasksParams): Promise<Task[]> {
    const qp = new URLSearchParams();
    if (params.associationRefs) {
      for (const ref of params.associationRefs) {
        qp.append('association_ref', ref);
      }
    }

    if (params.assignedTo) qp.set('assigned_to', params.assignedTo);
    if (params.status) qp.set('status', params.status);
    if (params.includeArchived) qp.set('include_archived', 'true');
    if (params.limit !== undefined) qp.set('limit', String(params.limit));
    if (params.offset !== undefined) qp.set('offset', String(params.offset));

    const raw = await this.get<RawTask[]>(
      `${this.basePath}/tasks?${qp.toString()}`
    );
    return raw.map((t) => this.toTask(t));
  }

  async getTask(taskId: string): Promise<Task> {
    const raw = await this.get<RawTask>(`${this.basePath}/tasks/${taskId}`);
    return this.toTask(raw);
  }

  async updateTask(taskId: string, body: UpdateTaskBody): Promise<Task> {
    const payload: Record<string, unknown> = {};
    if (body.title !== undefined) payload.title = body.title;
    if (body.description !== undefined) payload.description = body.description;
    if (body.status !== undefined) payload.status = body.status;
    if (body.assignedTo !== undefined) payload.assigned_to = body.assignedTo;
    if (body.dueDate !== undefined) payload.due_date = body.dueDate;

    const raw = await this.patch<RawTask>(
      `${this.basePath}/tasks/${taskId}`,
      payload
    );
    return this.toTask(raw);
  }

  async archiveTask(taskId: string): Promise<Task> {
    const raw = await this.post<RawTask>(
      `${this.basePath}/tasks/${taskId}/archive`,
      {}
    );
    return this.toTask(raw);
  }

  async deleteTask(taskId: string): Promise<void> {
    await this.delete(`${this.basePath}/tasks/${taskId}`);
  }

  /* eslint-disable @typescript-eslint/naming-convention */
  async getCategoryUsage(objectTypeId: string): Promise<{
    threadAssociationCount: number;
    typeOnlyThreadCount: number;
    fileAssociationCount: number;
    typeOnlyFileCount: number;
  }> {
    const raw = await this.get<{
      thread_association_count: number;
      type_only_thread_count: number;
      file_association_count: number;
      type_only_file_count: number;
    }>(`${this.basePath}/config/category-usage/${objectTypeId}`);
    return {
      threadAssociationCount: raw.thread_association_count,
      typeOnlyThreadCount: raw.type_only_thread_count,
      fileAssociationCount: raw.file_association_count,
      typeOnlyFileCount: raw.type_only_file_count,
    };
  }
  /* eslint-enable @typescript-eslint/naming-convention */

  // -- Tag candidates (review queue) -----------------------------------------

  async listTagCandidates(status = 'pending'): Promise<TagCandidate[]> {
    const raw = await this.get<{items: RawTagCandidate[]}>(
      `${this.basePath}/tag-candidates?status=${status}`
    );
    return raw.items.map((item) => toTagCandidate(item));
  }

  async acceptTagCandidate(
    candidateId: string,
    name?: string,
    fieldValues?: Record<string, unknown>
  ): Promise<AcceptTagCandidateResult> {
    /* eslint-disable @typescript-eslint/naming-convention */
    const raw = await this.post<{
      candidate: RawTagCandidate;
      outcome: string;
      promoted_count: number;
      skipped_count: number;
      required_fields?: RawTagMintField[];
      optional_fields?: RawTagMintField[];
      prefilled_values?: Record<string, unknown>;
    }>(`${this.basePath}/tag-candidates/${candidateId}/accept`, {
      ...(name ? {name} : {}),
      // Omit entirely rather than sending {} — the backend distinguishes "no field
      // values supplied" (first accept, may come back needs_fields) from a submission.
      ...(fieldValues ? {field_values: fieldValues} : {}),
    });
    /* eslint-enable @typescript-eslint/naming-convention */
    return {
      candidate: toTagCandidate(raw.candidate),
      outcome: raw.outcome,
      promotedCount: raw.promoted_count,
      skippedCount: raw.skipped_count,
      requiredFields: (raw.required_fields ?? []).map((f) => toTagMintField(f)),
      optionalFields: (raw.optional_fields ?? []).map((f) => toTagMintField(f)),
      prefilledValues: raw.prefilled_values ?? {},
    };
  }

  async rejectTagCandidate(candidateId: string): Promise<TagCandidate> {
    const raw = await this.post<RawTagCandidate>(
      `${this.basePath}/tag-candidates/${candidateId}/reject`,
      {}
    );
    return toTagCandidate(raw);
  }

  // -- Associations ----------------------------------------------------------

  async addAssociation(
    threadId: string,
    body: AddAssociationBody
  ): Promise<ThreadAssociation> {
    const raw = await this.post<RawThreadAssociation>(
      `${this.basePath}/threads/${threadId}/associations`,
      {object_type_id: body.objectTypeId, object_record_id: body.objectRecordId} // eslint-disable-line @typescript-eslint/naming-convention
    );
    return this.toAssociation(raw);
  }

  async createRecordAndAssociate(
    threadId: string,
    body: {objectTypeId: string; recordValues: Record<string, unknown>}
  ): Promise<{recordId: string; recordCode: string; associationId: string}> {
    const raw = await this.post<{
      record_id: string; // eslint-disable-line @typescript-eslint/naming-convention
      record_code: string; // eslint-disable-line @typescript-eslint/naming-convention
      association_id: string; // eslint-disable-line @typescript-eslint/naming-convention
    }>(`${this.basePath}/threads/${threadId}/create-record-and-associate`, {
      object_type_id: body.objectTypeId, // eslint-disable-line @typescript-eslint/naming-convention
      record_values: body.recordValues, // eslint-disable-line @typescript-eslint/naming-convention
    });
    return {
      recordId: raw.record_id,
      recordCode: raw.record_code,
      associationId: raw.association_id,
    };
  }

  async removeAssociation(
    threadId: string,
    associationId: string
  ): Promise<void> {
    await this.delete(
      `${this.basePath}/threads/${threadId}/associations/${associationId}`
    );
  }

  // -- Staged association suggestions -----------------------------------------

  async acceptSuggestion(
    threadId: string,
    suggestionId: string
  ): Promise<AcceptSuggestionResult> {
    const raw = await this.post<{
      association: RawThreadAssociation;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      thread_association_confirmed: boolean;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      thread_needs_review: boolean;
    }>(
      `${this.basePath}/threads/${threadId}/suggestions/${suggestionId}/accept`,
      {}
    );
    return {
      association: this.toAssociation(raw.association),
      threadAssociationConfirmed: raw.thread_association_confirmed,
      threadNeedsReview: raw.thread_needs_review,
    };
  }

  async rejectSuggestion(
    threadId: string,
    suggestionId: string
  ): Promise<void> {
    await this.post(
      `${this.basePath}/threads/${threadId}/suggestions/${suggestionId}/reject`,
      {}
    );
  }

  // -- Config ----------------------------------------------------------------

  async getRequiredAssociationTypes(): Promise<
    Array<{objectTypeId: string; name: string; prefix: string}>
  > {
    const raw = await this.get<RawRequiredAssociationTypesResponse>(
      `${this.basePath}/config/required-association-types`
    );
    return raw.required_association_types.map((t) => ({
      objectTypeId: t.object_type_id,
      name: t.name,
      prefix: t.prefix,
    }));
  }

  async updateRequiredAssociationTypes(
    objectTypeIds: string[]
  ): Promise<Array<{objectTypeId: string; name: string; prefix: string}>> {
    const raw = await this.put<RawRequiredAssociationTypesResponse>(
      `${this.basePath}/config/required-association-types`,
      {object_type_ids: objectTypeIds} // eslint-disable-line @typescript-eslint/naming-convention
    );
    return raw.required_association_types.map((t) => ({
      objectTypeId: t.object_type_id,
      name: t.name,
      prefix: t.prefix,
    }));
  }

  async getCategorizationOrgKnowledge(): Promise<string | null> {
    const raw = await this.get<{org_knowledge: string | null}>( // eslint-disable-line @typescript-eslint/naming-convention
      `${this.basePath}/config/categorization-org-knowledge`
    );
    return raw.org_knowledge;
  }

  async updateCategorizationOrgKnowledge(
    text: string | null
  ): Promise<string | null> {
    const raw = await this.put<{org_knowledge: string | null}>( // eslint-disable-line @typescript-eslint/naming-convention
      `${this.basePath}/config/categorization-org-knowledge`,
      {org_knowledge: text} // eslint-disable-line @typescript-eslint/naming-convention
    );
    return raw.org_knowledge;
  }

  // -- Trace -----------------------------------------------------------------

  async getTrace(params: TraceParams): Promise<TraceResponse> {
    const qp = new URLSearchParams();
    for (const ref of params.refs ?? []) {
      qp.append('association_ref', ref);
    }

    if (params.view) qp.set('view', params.view);
    if (params.refMode) qp.set('ref_mode', params.refMode);
    if (params.limit !== undefined) qp.set('limit', String(params.limit));
    if (params.offset !== undefined) qp.set('offset', String(params.offset));
    if (params.threadsBefore !== undefined)
      qp.set('threads_before', params.threadsBefore);
    if (params.recordsBefore !== undefined)
      qp.set('records_before', params.recordsBefore);
    if (params.filesBefore !== undefined)
      qp.set('files_before', params.filesBefore);
    if (params.paginate !== undefined)
      qp.set('paginate', String(params.paginate));

    const raw = await this.get<Record<string, unknown>>(
      `${this.basePath}/trace?${qp.toString()}`
    );
    return this.toTraceResponse(raw);
  }

  // -- Ingest ----------------------------------------------------------------

  async ingestEmail(body: IngestRequest): Promise<IngestResponse> {
    /* eslint-disable @typescript-eslint/naming-convention */
    const payload = {
      messages: body.messages.map((m) => ({
        raw_body: m.rawBody,
        sender: m.sender,
        recipients: m.recipients,
        subject: m.subject,
        sent_at: m.sentAt,
        attachments: (m.attachments ?? []).map((attachment) => ({
          filename: attachment.filename,
          content_type: attachment.contentType,
          size_bytes: attachment.sizeBytes,
          sha256: attachment.sha256,
          s3_key: attachment.s3Key,
        })),
        body_cleaned: m.bodyCleaned,
        hash_cleaned: m.hashCleaned,
        cleaner_version: m.cleanerVersion,
        cleaner_rules_fired: m.cleanerRulesFired,
        tail_start: m.tailStart,
        tail_end: m.tailEnd,
        tail_status: m.tailStatus,
        tail_confidence: m.tailConfidence,
        tail_reason: m.tailReason,
        tail_extractor_version: m.tailExtractorVersion,
        tail_body_hash: m.tailBodyHash,
      })),
      needs_review: body.needsReview,
      thread: body.thread
        ? {
            subject: body.thread.subject,
            associations: (body.thread.associations ?? []).map((a) => ({
              object_type_id: a.objectTypeId,
              object_record_id: a.objectRecordId,
            })),
          }
        : undefined,
    };
    /* eslint-enable @typescript-eslint/naming-convention */

    const raw = await this.post<RawIngestResponse>(
      `${this.basePath}/ingest`,
      payload
    );
    return {
      threadId: raw.thread_id,
      threadCreated: raw.thread_created,
      threadAssociationConfirmed: raw.thread_association_confirmed,
      messageIds: raw.message_ids,
      attachments: (raw.attachments ?? []).map(
        (a): IngestAttachmentSummary => ({
          id: a.id,
          emailMessageId: a.email_message_id,
          filename: a.filename,
          status: a.status as AttachmentStatus,
          filterReason: a.filter_reason,
        })
      ),
    };
  }

  // -- Email Attachments -----------------------------------------------------

  async listThreadAttachments(
    threadId: string,
    statuses?: AttachmentStatus[]
  ): Promise<ListAttachmentsResponse> {
    const qp = new URLSearchParams();
    if (statuses && statuses.length > 0) {
      for (const s of statuses) qp.append('statuses', s);
    }

    const qs = qp.toString();
    const url = qs
      ? `${this.basePath}/threads/${threadId}/attachments?${qs}`
      : `${this.basePath}/threads/${threadId}/attachments`;
    const raw = await this.get<{items: RawEmailAttachment[]}>(url);
    return {items: raw.items.map((a) => this.toAttachment(a))};
  }

  // -- Teams Attachments -----------------------------------------------------

  /**
   * List Teams attachments on a thread. Mapped into the shared `EmailAttachment`
   * shape (teams_message_id → emailMessageId) so the same attachment rendering
   * is reused. Default returns all statuses on the thread.
   */
  async listThreadTeamsAttachments(
    threadId: string,
    statuses?: AttachmentStatus[]
  ): Promise<ListAttachmentsResponse> {
    const qp = new URLSearchParams();
    if (statuses && statuses.length > 0) {
      for (const s of statuses) qp.append('statuses', s);
    }

    const qs = qp.toString();
    const url = qs
      ? `${this.basePath}/threads/${threadId}/teams-attachments?${qs}`
      : `${this.basePath}/threads/${threadId}/teams-attachments`;
    const raw = await this.get<{items: RawTeamsAttachment[]}>(url);
    return {items: raw.items.map((a) => this.toTeamsAttachment(a))};
  }

  async getTeamsAttachmentContentUrl(
    id: string
  ): Promise<AttachmentContentUrl> {
    const raw = await this.get<RawAttachmentContentUrl>(
      `${this.basePath}/teams-attachments/${id}/content-url`
    );
    return {url: raw.url, expiresAt: raw.expires_at};
  }

  async dismissTeamsAttachment(
    id: string,
    reason?: string
  ): Promise<EmailAttachment> {
    const raw = await this.post<RawTeamsAttachment>(
      `${this.basePath}/teams-attachments/${id}/dismiss`,
      reason ? {reason} : {}
    );
    return this.toTeamsAttachment(raw);
  }

  /**
   * Pending attachments enriched with thread id + subject. With no
   * `associationRefs` this is the tenant-wide triage feed; with refs it returns
   * the pending attachments across the threads of those object records in a
   * single request (the Library Files tab — no per-thread fan-out). Defaults to
   * pending_review only.
   */
  async listPendingTenantAttachments(
    statuses?: AttachmentStatus[],
    associationRefs?: string[]
  ): Promise<PendingTenantAttachment[]> {
    const qp = new URLSearchParams();
    if (statuses && statuses.length > 0) {
      for (const s of statuses) qp.append('status', s);
    }

    if (associationRefs && associationRefs.length > 0) {
      for (const ref of associationRefs) qp.append('association_ref', ref);
    }

    const qs = qp.toString();
    const url = qs
      ? `${this.basePath}/email-attachments?${qs}`
      : `${this.basePath}/email-attachments`;
    const raw = await this.get<{items: RawPendingTenantAttachment[]}>(url);
    return raw.items.map((a) => ({
      ...this.toAttachment(a),
      threadId: a.thread_id,
      threadSubject: a.thread_subject,
    }));
  }

  /**
   * Batch auto-promote pending attachments on a thread into managed Files.
   * Each item is independently validated; ineligible items are skipped.
   */
  async autoPromoteThreadAttachments(
    threadId: string,
    items: Array<{attachmentId: string; categoryId?: string; cui?: boolean}>
  ): Promise<AutoPromoteThreadAttachmentsResult> {
    /* eslint-disable @typescript-eslint/naming-convention */
    const payload = {
      attachments: items.map((i) => ({
        attachment_id: i.attachmentId,
        category_id: i.categoryId ?? null,
        cui: i.cui ?? false,
      })),
    };
    /* eslint-enable @typescript-eslint/naming-convention */
    const raw = await this.post<RawAutoPromoteResponse>(
      `${this.basePath}/threads/${threadId}/auto-promote-attachments`,
      payload
    );
    return {
      promoted: raw.promoted,
      skipped: raw.skipped,
      errors: raw.errors,
      results: raw.results.map((r) => ({
        attachmentId: r.attachment_id,
        fileId: r.file_id,
        status: r.status,
        reason: r.reason,
      })),
    };
  }

  async getAttachment(id: string): Promise<EmailAttachment> {
    const raw = await this.get<RawEmailAttachment>(
      `${this.basePath}/email-attachments/${id}`
    );
    return this.toAttachment(raw);
  }

  async getAttachmentContentUrl(id: string): Promise<AttachmentContentUrl> {
    const raw = await this.get<RawAttachmentContentUrl>(
      `${this.basePath}/email-attachments/${id}/content-url`
    );
    return {url: raw.url, expiresAt: raw.expires_at};
  }

  async dismissAttachment(
    id: string,
    reason?: string
  ): Promise<EmailAttachment> {
    const raw = await this.post<RawEmailAttachment>(
      `${this.basePath}/email-attachments/${id}/dismiss`,
      reason ? {reason} : {}
    );
    return this.toAttachment(raw);
  }

  async promoteAttachment(id: string): Promise<EmailAttachment> {
    const raw = await this.post<RawEmailAttachment>(
      `${this.basePath}/email-attachments/${id}/promote`,
      {}
    );
    return this.toAttachment(raw);
  }

  async deleteAttachment(id: string): Promise<void> {
    await this.delete(`${this.basePath}/email-attachments/${id}`);
  }

  /**
   * Reverse lookup: the source email/thread a Document or Record was created
   * from. Returns null when the resource did not originate from an attachment
   * (the endpoint 404s — expected for the vast majority of resources).
   */
  async getAttachmentSourceForResource(
    resourceId: string
  ): Promise<AttachmentSource | null> {
    try {
      const raw = await this.get<RawAttachmentSource>(
        `${this.basePath}/email-attachments/by-resource/${resourceId}`
      );
      return {
        attachmentId: raw.attachment_id,
        filename: raw.filename,
        threadId: raw.thread_id,
        emailSubject: raw.email_subject,
        emailSender: raw.email_sender,
        ingestedAt: raw.ingested_at,
      };
    } catch (error) {
      if ((error as {status?: number})?.status === 404) return null;
      throw error;
    }
  }

  // -- Notes -----------------------------------------------------------------

  async listNotesByThread(
    threadId: string,
    includeArchived?: boolean
  ): Promise<RelayNote[]> {
    const qp = new URLSearchParams();
    if (includeArchived) qp.set('include_archived', 'true');
    const qs = qp.toString();
    const raw = await this.get<RawRelayNote[] | {items: RawRelayNote[]}>(
      `${this.basePath}/threads/${threadId}/notes${qs ? `?${qs}` : ''}`
    );
    const items = Array.isArray(raw) ? raw : raw.items;
    return items.map((n) => this.toNote(n));
  }

  async createNote(body: CreateNoteBody): Promise<RelayNote> {
    /* eslint-disable @typescript-eslint/naming-convention */
    const payload: Record<string, unknown> = {body: body.body};
    if (body.threadId !== undefined) payload.thread_id = body.threadId;
    if (body.associations) {
      payload.associations = body.associations.map((a) => ({
        object_type_id: a.objectTypeId,
        object_record_id: a.objectRecordId,
      }));
    }

    if (body.fileIds) payload.file_ids = body.fileIds;
    if (body.createdAt !== undefined) payload.created_at = body.createdAt;
    /* eslint-enable @typescript-eslint/naming-convention */
    const raw = await this.post<RawRelayNote>(
      `${this.basePath}/notes`,
      payload
    );
    return this.toNote(raw);
  }

  async editNote(noteId: string, body: string): Promise<RelayNote> {
    const raw = await this.patch<RawRelayNote>(
      `${this.basePath}/notes/${noteId}`,
      {body}
    );
    return this.toNote(raw);
  }

  async archiveNote(noteId: string): Promise<RelayNote> {
    const raw = await this.post<RawRelayNote>(
      `${this.basePath}/notes/${noteId}/archive`,
      {}
    );
    return this.toNote(raw);
  }

  async hardDeleteNote(noteId: string): Promise<void> {
    await this.delete(`${this.basePath}/notes/${noteId}`);
  }

  async attachFileToNote(noteId: string, fileId: string): Promise<RelayNote> {
    const raw = await this.post<RawRelayNote>(
      `${this.basePath}/notes/${noteId}/files`,
      {file_id: fileId} // eslint-disable-line @typescript-eslint/naming-convention
    );
    return this.toNote(raw);
  }

  async detachFileFromNote(noteId: string, refId: string): Promise<void> {
    await this.delete(`${this.basePath}/notes/${noteId}/files/${refId}`);
  }

  // -- Pins ------------------------------------------------------------------

  async listPins(): Promise<RelayPin[]> {
    const raw = await this.get<RawPin[]>(`${this.basePath}/pins`);
    return raw.map((p) => this.toPin(p));
  }

  async createPin(body: CreatePinBody): Promise<RelayPin> {
    /* eslint-disable @typescript-eslint/naming-convention */
    const payload = {
      label: body.label,
      kind: body.kind,
      payload: {
        refs: body.payload.refs.map((r) => ({
          object_type_id: r.objectTypeId,
          object_record_id: r.objectRecordId,
        })),
      },
    };
    /* eslint-enable @typescript-eslint/naming-convention */
    const raw = await this.post<RawPin>(`${this.basePath}/pins`, payload);
    return this.toPin(raw);
  }

  async updatePin(pinId: string, body: UpdatePinBody): Promise<RelayPin> {
    const raw = await this.patch<RawPin>(`${this.basePath}/pins/${pinId}`, {
      label: body.label,
    });
    return this.toPin(raw);
  }

  async deletePin(pinId: string): Promise<void> {
    await this.delete(`${this.basePath}/pins/${pinId}`);
  }

  // -- Private converters (static for purity) --------------------------------

  private toThread(r: RawThread): Thread {
    return {
      id: r.id,
      tenantId: r.tenant_id,
      subject: r.subject,
      associations: (r.associations ?? []).map((a) => this.toAssociation(a)),
      pendingSuggestions: (r.pending_suggestions ?? []).map((s) =>
        this.toSuggestion(s)
      ),
      latestSignal: r.latest_signal
        ? {
            id: r.latest_signal.id,
            content: r.latest_signal.content,
            status: r.latest_signal.status as SignalStatus,
            createdAt: r.latest_signal.created_at,
          }
        : null,
      signalCount: r.signal_count ?? 0,
      teamsMessageCount: r.teams_message_count ?? 0,
      associationConfirmed: r.association_confirmed,
      autoConfirmed: r.auto_confirmed ?? false,
      needsReview: r.needs_review,
      confirmedBy: r.confirmed_by,
      confirmedAt: r.confirmed_at,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      lastContentActivityAt: r.last_content_activity_at ?? null,
      deletedAt: r.deleted_at ?? null,
      // `kind` is cast rather than validated: an unrecognized value from a
      // newer backend falls through to the renderer's default branch, which
      // says "Added by Matic" instead of blanking the line.
      ingestProvenance: r.ingest_provenance
        ? {
            kind: r.ingest_provenance.kind as ThreadIngestProvenance['kind'],
            detail: r.ingest_provenance.detail,
            direction: r.ingest_provenance
              .direction as ThreadIngestProvenance['direction'],
            decidedAt: r.ingest_provenance.decided_at,
          }
        : null,
    };
  }

  private normalizeThreadList(
    raw:
      | RawThread[]
      | {
          items: RawThread[];
          // eslint-disable-next-line @typescript-eslint/naming-convention
          total_count?: number;
          limit?: number;
          offset?: number;
        }
  ): ThreadListResponse {
    if (Array.isArray(raw)) {
      const items = raw.map((t) => this.toThread(t));
      return {items, totalCount: items.length, limit: items.length, offset: 0};
    }

    const items = raw.items.map((t) => this.toThread(t));
    return {
      items,
      totalCount: raw.total_count ?? items.length,
      limit: raw.limit ?? items.length,
      offset: raw.offset ?? 0,
    };
  }

  private toMessage(r: RawMessage): EmailMessage {
    return {
      id: r.id,
      threadId: r.thread_id,
      sender: r.sender,
      recipients: r.recipients,
      subject: r.subject,
      sentAt: r.sent_at,
      rawBody: r.raw_body,
      rawHeaders: r.raw_headers,
      reviewedAt: r.reviewed_at,
      reviewedBy: r.reviewed_by,
      classificationSource: r.classification_source,
      bodyCleaned: r.body_cleaned,
      hashCleaned: r.hash_cleaned,
      cleanerVersion: r.cleaner_version,
      cleanerRulesFired: r.cleaner_rules_fired,
      tailStart: r.tail_start,
      tailEnd: r.tail_end,
      tailStatus: r.tail_status as EmailMessage['tailStatus'],
      tailConfidence: r.tail_confidence as EmailMessage['tailConfidence'],
      tailReason: r.tail_reason,
      tailExtractorVersion: r.tail_extractor_version,
      tailBodyHash: r.tail_body_hash,
      messageCategory: r.message_category ?? null,
      calendar: this.toCalendarMeta(r.calendar),
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    };
  }

  private toTeamsMessage(r: RawTeamsMessage): TeamsMessage {
    return {
      id: r.id,
      tenantId: r.tenant_id,
      threadId: r.thread_id,
      teamsMessageId: r.teams_message_id,
      replyToId: r.reply_to_id,
      surfaceKind: r.surface_kind,
      teamsConversationId: r.teams_conversation_id,
      conversationLabel: r.conversation_label,
      authorDisplayName: r.author_display_name,
      authorAadId: r.author_aad_id,
      authorUpn: r.author_upn,
      bodyText: r.body_text,
      sentAt: r.sent_at,
      webUrl: r.web_url,
      ingestedByUserId: r.ingested_by_user_id,
      extraMetadata: r.extra_metadata,
      createdAt: r.created_at,
    };
  }

  private toCalendarMeta(r: RawCalendarMeta | null): CalendarMeta | null {
    if (!r) return null;
    return {
      uid: r.uid,
      method: r.method,
      status: r.status,
      summary: r.summary,
      dtstart: r.dtstart,
      dtend: r.dtend,
      allDay: r.all_day,
      location: r.location,
      organizer: r.organizer
        ? {name: r.organizer.name, email: r.organizer.email}
        : null,
      attendees: (r.attendees ?? []).map((a) => ({
        name: a.name,
        email: a.email,
        partstat: a.partstat,
      })),
    };
  }

  private toTask(r: RawTask): Task {
    return {
      id: r.id,
      tenantId: r.tenant_id,
      threadId: r.thread_id,
      sourceSignalId: r.source_signal_id,
      sourceMessageId: r.source_message_id,
      title: r.title,
      description: r.description,
      status: r.status as Task['status'],
      assignedTo: r.assigned_to,
      dueDate: r.due_date,
      createdBy: r.created_by,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      deletedAt: r.deleted_at,
    };
  }

  private toSignal(r: RawSignal): Signal {
    return {
      id: r.id,
      threadId: r.thread_id,
      status: r.status as Signal['status'],
      content: r.content,
      sourceMessageIds: r.source_message_ids,
      resolvedBy: r.resolved_by,
      resolvedAt: r.resolved_at,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      effectiveTimestamp: r.effective_timestamp,
    };
  }

  private toAttachment(r: RawEmailAttachment): EmailAttachment {
    return {
      id: r.id,
      tenantId: r.tenant_id,
      emailMessageId: r.email_message_id,
      source: 'email',
      filename: r.filename,
      contentType: r.content_type,
      sizeBytes: r.size_bytes,
      sha256: r.sha256,
      status: r.status as AttachmentStatus,
      imageSignal: r.image_signal,
      filterReason: r.filter_reason,
      ingestedResourceKind:
        r.ingested_resource_kind as AttachmentResourceKind | null,
      ingestedResourceId: r.ingested_resource_id,
      ingestedAt: r.ingested_at,
      ingestedBy: r.ingested_by,
      dismissedAt: r.dismissed_at,
      dismissedBy: r.dismissed_by,
      dismissReason: r.dismiss_reason,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    };
  }

  private toTeamsAttachment(r: RawTeamsAttachment): EmailAttachment {
    return {
      id: r.id,
      tenantId: r.tenant_id,
      // Reuse the email attachment shape — the source message id lives in the
      // same field so the shared attachment rendering works unchanged.
      emailMessageId: r.teams_message_id,
      source: 'teams',
      filename: r.filename,
      contentType: r.content_type,
      sizeBytes: r.size_bytes,
      sha256: r.sha256,
      status: r.status as AttachmentStatus,
      imageSignal: null,
      filterReason: r.filter_reason,
      ingestedResourceKind:
        r.ingested_resource_kind as AttachmentResourceKind | null,
      ingestedResourceId: r.ingested_resource_id,
      ingestedAt: r.ingested_at,
      ingestedBy: r.ingested_by,
      dismissedAt: r.dismissed_at,
      dismissedBy: r.dismissed_by,
      dismissReason: r.dismiss_reason,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    };
  }

  private toAssociation(r: RawThreadAssociation): ThreadAssociation {
    return {
      id: r.id,
      objectTypeId: r.object_type_id,
      objectTypeName: r.object_type_name,
      objectRecordId: r.object_record_id,
      objectRecordCode: r.object_record_code,
      objectRecordName: r.object_record_name,
      isRequiredType: r.is_required_type,
      confidence: r.confidence ?? null,
      confidenceBand: r.confidence_band ?? 'low',
      classificationSource: r.classification_source ?? 'manual',
      reason: r.reason ?? null,
      autoConfirmedAt: r.auto_confirmed_at ?? null,
      createdAt: r.created_at,
      createdBy: r.created_by,
    };
  }

  private toSuggestion(r: RawAssociationSuggestion): AssociationSuggestion {
    return {
      id: r.id,
      threadId: r.thread_id,
      kind: r.kind,
      objectTypeId: r.object_type_id,
      objectTypeName: r.object_type_name,
      objectRecordId: r.object_record_id,
      objectRecordCode: r.object_record_code,
      objectRecordName: r.object_record_name,
      confidence: r.confidence,
      confidenceBand: r.confidence_band,
      reason: r.reason,
      status: r.status,
    };
  }

  private toTraceResponse(raw: Record<string, unknown>): TraceResponse {
    const rawThreads = raw.threads as unknown[] | undefined;
    const rawRefs =
      (raw.refs as Array<Record<string, string>> | undefined) ?? [];
    return {
      refs: rawRefs.map((r) => ({
        objectTypeId: r.object_type_id,
        objectRecordId: r.object_record_id,
      })),
      view: (raw.view as 'compact' | 'detailed') ?? 'compact',
      threads: rawThreads
        ? rawThreads.map((t) =>
            this.toTraceThreadCard(t as Record<string, unknown>)
          )
        : null,
      // TODO: `items` (timeline view) needs snake_case → camelCase mapping when
      // the detailed view is implemented. Currently always null (compact view).
      items: (raw.items as TraceResponse['items']) ?? null,
      relatedRecords: (
        (raw.related_records as Array<Record<string, unknown>>) ?? []
      ).map((r) => ({
        recordId: r.record_id as string,
        displayId: r.display_id as string,
        name: (r.name as string) ?? '',
        typeName: r.type_name as string,
        status: r.status as string,
        createdAt: r.created_at as string,
      })),
      files: ((raw.files as Array<Record<string, unknown>>) ?? []).map((f) => ({
        fileId: f.file_id as string,
        name: f.name as string,
        originalFilename: f.original_filename as string,
        contentType: f.content_type as string,
        sizeBytes: f.size_bytes as number,
        categoryId: (f.category_id as string) ?? null,
        createdAt: f.created_at as string,
      })),
      hasMore: (raw.has_more as boolean) ?? false,
      limit: (raw.limit as number) ?? 25,
      offset: (raw.offset as number) ?? 0,
      nextThreadsBefore: (raw.next_threads_before as string) ?? null,
      nextRecordsBefore: (raw.next_records_before as string) ?? null,
      nextFilesBefore: (raw.next_files_before as string) ?? null,
    };
  }

  private toTraceThreadCard(raw: Record<string, unknown>): TraceThreadCard {
    const t = raw.thread as Record<string, unknown>;
    const lm = raw.latest_message as Record<string, unknown> | null;

    return {
      thread: {
        id: t.id as string,
        subject: (t.subject as string | null) ?? null,
        associations: ((t.associations as RawThreadAssociation[]) ?? []).map(
          (a) => this.toAssociation(a)
        ),
        associationConfirmed: (t.association_confirmed as boolean) ?? false,
        autoConfirmed: (t.auto_confirmed as boolean) ?? false,
        needsReview: (t.needs_review as boolean) ?? false,
        createdAt: t.created_at as string,
        updatedAt: t.updated_at as string,
      },
      signals: ((raw.signals as RawSignal[]) ?? []).map(
        (s): TraceSignal => ({
          id: s.id,
          threadId: s.thread_id,
          status: s.status as TraceSignal['status'],
          content: s.content,
          sourceMessageIds: s.source_message_ids,
          resolvedBy: s.resolved_by,
          resolvedAt: s.resolved_at,
          createdAt: s.created_at,
          updatedAt: s.updated_at,
          effectiveTimestamp: s.effective_timestamp,
        })
      ),
      tasks: ((raw.tasks as Array<Record<string, unknown>>) ?? []).map(
        (tk): TraceTask => ({
          id: tk.id as string,
          threadId: tk.thread_id as string,
          title: tk.title as string,
          description: (tk.description as string | null) ?? null,
          status: tk.status as TraceTask['status'],
          assignedTo: (tk.assigned_to as string | null) ?? null,
          dueDate: (tk.due_date as string | null) ?? null,
          createdBy: tk.created_by as string,
          createdAt: tk.created_at as string,
          updatedAt: tk.updated_at as string,
          deletedAt: (tk.deleted_at as string | null) ?? null,
        })
      ),
      latestMessage: lm ? this.toMessage(lm as unknown as RawMessage) : null,
      messageCount: (raw.message_count as number) ?? 0,
      onlyCalendarMessages: (raw.only_calendar_messages as boolean) ?? false,
      lastActivityAt: raw.last_activity_at as string,
      pendingAttachmentCount: (raw.pending_attachment_count as number) ?? 0,
      fileCount: (raw.file_count as number) ?? 0,
      noteCount: (raw.note_count as number) ?? 0,
      latestNote: raw.latest_note
        ? this.toTraceNoteSummary(raw.latest_note as RawTraceNoteSummary)
        : null,
      ingestedFiles: (
        (raw.ingested_files as Array<Record<string, unknown>>) ?? []
      ).map((f) => ({
        fileId: f.file_id as string,
        attachedAt: f.attached_at as string,
      })),
      teamsMessageCount: (raw.teams_message_count as number) ?? 0,
      latestTeamsMessage: raw.latest_teams_message
        ? this.toTeamsMessageSummary(
            raw.latest_teams_message as Record<string, unknown>
          )
        : null,
    };
  }

  private toTeamsMessageSummary(
    r: Record<string, unknown>
  ): TeamsMessageSummary {
    return {
      id: r.id as string,
      authorDisplayName: r.author_display_name as string,
      surfaceKind: r.surface_kind as TeamsMessageSummary['surfaceKind'],
      conversationLabel: (r.conversation_label as string | null) ?? null,
      bodyPreview: (r.body_preview as string | null) ?? null,
      sentAt: r.sent_at as string,
      webUrl: (r.web_url as string | null) ?? null,
    };
  }

  private toNote(r: RawRelayNote): RelayNote {
    return {
      id: r.id,
      body: r.body,
      author: {id: r.author.id, displayName: r.author.display_name},
      associations: (r.associations ?? []).map((a) => ({
        objectTypeId: a.object_type_id,
        objectTypeName: a.object_type_name,
        objectRecordId: a.object_record_id,
        objectRecordCode: a.object_record_code,
        objectRecordName: a.object_record_name,
      })),
      fileRefs: (r.file_refs ?? []).map((f) => ({
        refId: f.ref_id,
        fileId: f.file_id,
        fileName: f.file_name,
        contentType: f.content_type,
      })),
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      deletedAt: r.deleted_at,
    };
  }

  private toTraceNoteSummary(r: RawTraceNoteSummary): TraceNoteSummary {
    return {
      id: r.id,
      authorUserId: r.author_user_id,
      bodyPreview: r.body_preview,
      fileRefCount: r.file_ref_count,
      fileIds: r.file_ids ?? [],
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    };
  }

  private toPin(r: RawPin): RelayPin {
    return {
      id: r.id,
      label: r.label,
      kind: r.kind,
      payload: {
        refs: r.payload.refs.map((ref) => ({
          objectTypeId: ref.object_type_id,
          objectRecordId: ref.object_record_id,
        })),
      },
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    };
  }
}

// Singleton factory
let cachedRelayApi: RelayApiClient | undefined;

function toTagCandidateMention(
  raw: RawTagCandidateMention
): TagCandidateMention {
  return {
    id: raw.id,
    threadId: raw.thread_id,
    threadSubject: raw.thread_subject,
    associationId: raw.association_id,
    confidence: raw.confidence,
    rationale: raw.rationale,
    status: raw.status,
    createdAt: raw.created_at,
  };
}

function toTagMintField(raw: RawTagMintField): TagMintField {
  return {
    fieldKey: raw.field_key,
    label: raw.label,
    fieldType: raw.field_type,
    options: raw.options ?? [],
  };
}

function toTagCandidate(raw: RawTagCandidate): TagCandidate {
  return {
    id: raw.id,
    objectTypeId: raw.object_type_id,
    objectTypeName: raw.object_type_name,
    displayName: raw.display_name,
    bestConfidence: raw.best_confidence,
    rationale: raw.rationale,
    status: raw.status,
    mintedRecordId: raw.minted_record_id,
    mentionCount: raw.mention_count,
    threadCount: raw.thread_count,
    distinctSenderCount: raw.distinct_sender_count,
    firstSeenAt: raw.first_seen_at,
    lastSeenAt: raw.last_seen_at,
    createdAt: raw.created_at,
    resolvedAt: raw.resolved_at,
    mentions: raw.mentions.map((m) => toTagCandidateMention(m)),
  };
}

export function getRelayApi(token?: string): RelayApiClient {
  if (token) return new RelayApiClient(token);
  cachedRelayApi ??= new RelayApiClient();
  return cachedRelayApi;
}
